const toNum = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

// Kept in sync with backend/services/macoCalculator.js. Per-day suffixes
// ("mcg/day") carry no conversion; per-kg units only apply to LD50 (ld50ToMg).
const normalizeUnit = (unit) => String(unit || 'mg').trim().toLowerCase()
    .replace(/μ/g, 'µ').replace(/\s+/g, '').replace(/\/(day|d)$/, '');

const unitToMgFactor = (unit) => {
    const normalized = normalizeUnit(unit);
    if (normalized === 'kg') return 1000000;
    if (normalized === 'g') return 1000;
    if (normalized === 'mcg' || normalized === 'ug' || normalized === 'µg') return 0.001;
    return normalized === 'mg' ? 1 : NaN;
};

export const toMg = (value, unit) => {
    const factor = unitToMgFactor(unit);
    return Number.isFinite(factor) ? toNum(value, 0) * factor : 0;
};

// Fixed empirical factor converting an LD50 to a NOEL (Layton et al., 1987).
export const LD50_TO_NOEL_FACTOR = 0.0005;

export const ld50ToMg = (value, unit, bodyWeightKg = 70) => {
    const normalized = normalizeUnit(unit);
    const perKg = normalized.endsWith('/kg');
    const mg = toMg(value, perKg ? normalized.slice(0, -3) : normalized);
    return perKg ? mg * Math.max(0.000001, toNum(bodyWeightKg, 70)) : mg;
};

export const calculateMacoValues = ({
  previousApi,
  nextProduct,
  hbelSafetyFactor,
  doseSafetyFactor,
  ppmLimit,
  toxSafetyFactor,
  detergentSafetyFactor = 2000,
  detergentLd50 = 0,
  detergentBodyWeight = 70,
  detergentEnabled = false,
  criteriaEnabled,
  surfaceAreaCm2,
}) => {
  if (!previousApi || !nextProduct) {
    return {
      hbel: 0, dose: 0, ppm: 0, tox: 0, detergent: 0, governing: 0,
      hbelMar: 0, doseMar: 0, ppmMar: 0, toxMar: 0, detergentMar: 0, governingMar: 0,
      detergentAde: 0,
    };
  }

  const enabled = { hbel: true, dose: true, ppm: true, tox: true, detergent: false, ...(criteriaEnabled || {}) };

  const pdePrevMg = toMg(previousApi?.pde_value, previousApi?.pde_unit || 'mg');
  const tddPrevMg = toMg(previousApi?.therapeutic_dose, previousApi?.therapeutic_dose_unit || 'mg');
  const toxPrevMg = ld50ToMg(previousApi?.ld50_value, previousApi?.ld50_unit || 'mg/kg', detergentBodyWeight) * LD50_TO_NOEL_FACTOR;

  const mbsMg = toMg(nextProduct?.batch_size, nextProduct?.batch_size_unit || 'mg');
  const maxDailyDoseMg = Math.max(0, ...(Array.isArray(nextProduct?.apis) ? nextProduct.apis : [])
    .map(a => toMg(a.max_daily_dose, a.max_daily_dose_unit || 'mg'))
    .filter(v => v > 0));
  const hasLdd = maxDailyDoseMg > 0;

  const mbsKg = mbsMg / 1000000;
  const safeLdd = Math.max(0.000001, maxDailyDoseMg);

  const hbel = pdePrevMg > 0 && mbsMg > 0 && hasLdd ? (pdePrevMg * mbsMg) / (Math.max(hbelSafetyFactor, 0.000001) * safeLdd) : 0;
  const dose = tddPrevMg > 0 && mbsMg > 0 && hasLdd ? (tddPrevMg * mbsMg) / (Math.max(doseSafetyFactor, 0.000001) * safeLdd) : 0;
  const ppm = mbsMg > 0 ? Math.max(0, toNum(ppmLimit, 0)) * mbsKg : 0;
  const tox = toxPrevMg > 0 && mbsMg > 0 && hasLdd ? (toxPrevMg * mbsMg) / (Math.max(toxSafetyFactor, 0.000001) * safeLdd) : 0;
  const detergentAde = detergentEnabled
    ? (toNum(detergentLd50, 0) * Math.max(toNum(detergentBodyWeight, 70), 0.000001)) / Math.max(toNum(detergentSafetyFactor, 2000), 0.000001)
    : 0;
  const detergent = detergentAde > 0 && mbsMg > 0 && hasLdd ? (detergentAde * mbsMg) / safeLdd : 0;
  // Detergent (the cleaning agent) is tracked separately and never competes for "governing" —
  // it's a different contaminant from the drug-substance-based criteria below, so letting it
  // win the min() would silently turn the product's residue limit into a detergent limit.
  // Kept in sync with backend/services/macoCalculator.js.
  const detergentActive = enabled.detergent && detergentEnabled && detergent > 0;

  const enabledValues = [
    enabled.hbel ? hbel : 0,
    enabled.dose ? dose : 0,
    enabled.ppm ? ppm : 0,
    enabled.tox ? tox : 0,
  ].filter(v => v > 0);
  const governing = enabledValues.length ? Math.min(...enabledValues) : 0;

  const safeArea = Math.max(0.000001, surfaceAreaCm2);
  const hbelMar = (hbel * 1000) / safeArea;
  const doseMar = (dose * 1000) / safeArea;
  const ppmMar = (ppm * 1000) / safeArea;
  const toxMar = (tox * 1000) / safeArea;
  const detergentMar = (detergent * 1000) / safeArea;

  const enabledMar = [
    enabled.hbel && hbelMar > 0 ? hbelMar : 0,
    enabled.dose && doseMar > 0 ? doseMar : 0,
    enabled.ppm && ppmMar > 0 ? ppmMar : 0,
    enabled.tox && toxMar > 0 ? toxMar : 0,
  ].filter(v => v > 0);
  const governingMar = enabledMar.length ? Math.min(...enabledMar) : 0;

  return { hbel, dose, ppm, tox, detergent, detergentAde, detergentActive, governing, hbelMar, doseMar, ppmMar, toxMar, detergentMar, governingMar };
};
